import {Injectable} from "@angular/core";

import {HttpClient, HttpHeaders, HttpParams} from "@angular/common/http";
import {LoggerService} from "../logger.service";
import {Observable} from "rxjs";
import {AppConfig as ApplicationConfig} from "../app.config";
import {
  ChildNodesPayload,
  UserAuth,
  BaseSFTPAPIResponse,
  DeleteNodePayload,
  UploadFileResponse,
  UserInfoResponse,
  MessageResponse,
} from "@app/services/sftp-api/models";
import {TreeNode} from "@app/modules/models";

export enum UserAccessRightType {
  DEFAULT = "DEFAULT",
  READ_ONLY = "READ_ONLY",
  READ_WRITE = "READ_WRITE",
}

export interface UserSettings {
  username: string;
  access_rights: UserAccessRightType;
}

@Injectable({
  providedIn: "root",
})
export class SFTPAPIService {
  constructor(private logger: LoggerService, private appConfig: ApplicationConfig, private httpClient: HttpClient) {}

  private get baseURL(): string {
    return this.appConfig.settings.api.url;
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  isConnected(): Observable<any> {
    return this.httpClient.get(`${this.baseURL}/api/isconnected`, {withCredentials: true});
  }

  getUserSettings(): Observable<UserSettings> {
    return this.httpClient.get<UserSettings>(`${this.baseURL}/api/users/settings`, {withCredentials: true});
  }

  // eslint-disable-next-line @typescript-eslint/no-explicit-any
  authenticate(payload: UserAuth): Observable<any> {
    const combined = `${payload.username} ${payload.password}`;

    const headers = {
      "Content-Type": "application/json",
      Accept: "application/json",
      Authorization: "Basic " + btoa(combined.replace(/['"]+/g, "")),
    };

    const requestOptions = {
      headers: new HttpHeaders(headers),
      withCredentials: true,
    };

    return this.httpClient.post(`${this.baseURL}/api/authenticate`, {}, requestOptions);
  }

  uploadFile(input: FormData, forceOverrideFile: boolean): Observable<UploadFileResponse> {
    const params = new HttpParams().set("force_override", forceOverrideFile);

    return this.httpClient.post<UploadFileResponse>(`${this.baseURL}/api/upload`, input, {
      withCredentials: true,
      params: params,
    });
  }

  downloadFile(input) {
    return this.httpClient.post(`${this.baseURL}/api/download`, input, {
      responseType: "arraybuffer",
      withCredentials: true,
    });
  }

  downloadFilesBatch(input) {
    return this.httpClient.post(`${this.baseURL}/api/download_batch`, input, {
      responseType: "arraybuffer",
      withCredentials: true,
    });
  }

  deleteNode(input: DeleteNodePayload): Observable<void> {
    return this.httpClient.post<void>(`${this.baseURL}/api/delete`, input, {withCredentials: true});
  }

  getChildNodes(input: ChildNodesPayload): Observable<BaseSFTPAPIResponse<TreeNode[]>> {
    return this.httpClient.post<BaseSFTPAPIResponse<TreeNode[]>>(`${this.baseURL}/api/listchildnodes`, input, {
      withCredentials: true,
    });
  }

  logout(): Observable<void> {
    return this.httpClient.post<void>(`${this.baseURL}/api/logout`, {}, {withCredentials: true});
  }

  getUserInfo(): Observable<UserInfoResponse> {
    return this.httpClient.get<UserInfoResponse>(`${this.baseURL}/api/users/account_info`, {
      withCredentials: true,
    });
  }

  updateUserEmail(email: string): Observable<MessageResponse> {
    return this.httpClient.post<MessageResponse>(
      `${this.baseURL}/api/users/set_email`,
      {email},
      {
        withCredentials: true,
      }
    );
  }

  verifyUserEmail(code: string): Observable<MessageResponse> {
    return this.httpClient.post<MessageResponse>(
      `${this.baseURL}/api/users/verify_email`,
      {code},
      {
        withCredentials: true,
      }
    );
  }

  uploadPubKeyFile(input: FormData): Observable<MessageResponse> {
    return this.httpClient.post<MessageResponse>(`${this.baseURL}/api/users/set_ssh_key`, input, {
      withCredentials: true,
    });
  }
}
